import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { PieChart } from '@mui/x-charts/PieChart';
import {
    Bike,
    Car,
    CalendarDays,
    CheckCircle2,
    CircleDot,
    Factory,
    Gauge,
    Wrench,
    DollarSign,
    Layers,
    Motorbike,
    Palette,
    PieChart as PieChartIcon,
    RefreshCw,
    TrendingUp,
    Wallet,
    XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import AnimatedNumber from '../../component/common/AnimatedNumber';
import DefaultTable from '../../component/table/DefaultTable';
import './dashboard.css';
import { useAppBarAction, useAppBarSubtitle } from '../../component/layout/AppBarActions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import usePermissions from '../../hooks/usePermissions';
import { KitCard, KitItem, KitList, KitPill, SecH } from '../../component/common/MobileKit';
import { KitKV } from '../../component/common/MobileKitExtras';
import { protoIcon } from '../../component/common/ProtoIcon';
import { HBars, Kpi, KpiGrid, KitEmptyNote } from '../../component/common/MobileDashboardKit';

const EMPTY_VEHICLE_DATA = {
    total_vehicles: 0,
    active_vehicles: 0,
    inactive_vehicles: 0,
    total_cars: 0,
    total_motorcycles: 0,
    total_bicycles: 0,
    total_other_vehicles: 0,
    average_kilometer: 0,
    average_age: 0,
    most_popular_color: '',
    most_popular_manufacturer: '',
    cars_with_most_services: [],
    cars_with_most_expensive_services: [],
};

// Card header (`.card-head`): 36px icon chip (tinted bg/fg) + title + one
// line subtitle — DESIGN-SYSTEM.md §5.2. Local copy of the same pattern
// already used by SaleDashboard.js/OrderDashboard.js.
function CardHeadRow({ icon, iconBg, iconFg, title, subtitle, loading }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
            <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: iconBg,
                color: iconFg,
            }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: '1px' }}>
                    {loading ? <Skeleton variant="text" width={140} /> : subtitle}
                </Typography>
            </Box>
        </Box>
    );
}

const MONTH_ABBR_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const normalizeText = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

// `/chart/vehicle/values` devolve os totais como texto numérico do Postgres
// ("1234.5000000"); aceita também o formato pt-BR ("1.234,50").
const parseDecimal = (v) => {
    if (typeof v === 'number') return v;
    const str = String(v ?? '0').trim();
    if (/^-?\d+(\.\d+)?$/.test(str)) return Number(str);
    const n = Number(str.replace(/[^\d,-]/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
};

const formatPct = (value) => `${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`;

// `.tk` — ticker da faixa "Indicadores da frota"
// (`prototipo-dashboard-veiculos.html`): borda esquerda colorida, ícone 20px +
// rótulo, valor 20px (30px no hero) e nota tracejada no rodapé.
function FleetTicker({ icon, iconBg, iconFg, accent, label, value, note, loading }) {
    return (
        <Card sx={{ borderLeft: '3px solid', borderLeftColor: accent, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <CardContent sx={{ p: '12px 14px 10px', '&:last-child': { pb: '10px' }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mb: '6px', fontSize: '11px', fontWeight: 600, color: 'text.secondary' }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: iconBg, color: iconFg }}>
                        {icon}
                    </Box>
                    {label}
                </Box>
                {loading ? (
                    <Skeleton variant="text" width={110} height={28} />
                ) : (
                    <Typography sx={{ fontSize: '20px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, color: 'text.primary', fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
                        {value}
                    </Typography>
                )}
                <Box sx={{ flex: 1, minHeight: '8px' }} />
                <Box sx={{ fontSize: '10.5px', color: 'text.disabled', pt: '6px', borderTop: '1px dashed', borderColor: 'divider', minHeight: 22, display: 'flex', alignItems: 'center', '& b': { color: 'text.secondary', fontWeight: 600 } }}>
                    <span>{note}</span>
                </Box>
            </CardContent>
        </Card>
    );
}

// Linha de resumo do hero (`.summary-row`): chip 26px + rótulo + "% da frota" + valor.
function SummaryRow({ icon, iconBg, iconFg, label, share, value, loading }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, py: '7px', fontSize: '13px', color: 'text.secondary' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
                <Box sx={{ width: 26, height: 26, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: iconBg, color: iconFg }}>
                    {icon}
                </Box>
                {label}
                {!loading && share && <Box component="small" sx={{ fontSize: '10.5px', color: 'text.disabled', fontWeight: 500, ml: '2px' }}>{share}</Box>}
            </Box>
            {loading ? <Skeleton variant="text" width={40} height={22} /> : (
                <Typography component="b" sx={{ fontSize: '15px', fontWeight: 800, color: 'text.primary', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
            )}
        </Box>
    );
}

// `.mbars` + `.mlabels` — cadastros por mês do ano corrente (12 colunas).
// Meses futuros ficam cinza; o pico recebe gradiente.
function MonthlyBars({ values, currentMonth }) {
    const max = Math.max(...values, 0);
    const peakIdx = max > 0 ? values.indexOf(max) : -1;
    return (
        <>
            <Box title="Cadastros por mês no ano corrente" sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '5px', alignItems: 'end', height: 96, mt: 1.5, mb: '4px' }}>
                {values.map((v, i) => {
                    const future = i > currentMonth;
                    return (
                        <Box
                            key={MONTH_ABBR_LABELS[i]}
                            component="span"
                            title={`${MONTH_ABBR_LABELS[i]}: ${v} ${v === 1 ? 'cadastro' : 'cadastros'}`}
                            sx={{
                                display: 'block',
                                borderRadius: '4px 4px 2px 2px',
                                minHeight: '3px',
                                height: max > 0 && !future ? `${Math.max((v / max) * 100, 3)}%` : '3%',
                                ...(future
                                    ? { bgcolor: 'divider' }
                                    : i === peakIdx
                                        ? { background: (t) => t.palette.gradient.brand }
                                        : { bgcolor: 'primary.main', opacity: 0.85 }),
                            }}
                        />
                    );
                })}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '5px', fontSize: '9.5px', color: 'text.disabled', textAlign: 'center', mb: 1.5 }}>
                {MONTH_ABBR_LABELS.map((m) => <span key={m}>{m}</span>)}
            </Box>
        </>
    );
}

// `.metric-row` — 32/34px icon chip + stacked [uppercase label / bold value].
// Local copy of OrderDashboard.js's MetricRow, reused both for "Métricas
// Gerais" and "Tipos de Veículos" (prototipo-dashboard-veiculos.html).
function MetricRow({ icon, iconBg, iconFg, label, value, share, loading }) {
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: '11px 10px',
            borderRadius: '10px',
            transition: 'background-color .15s ease',
            '&:hover': { bgcolor: 'background.subtle' },
        }}>
            <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '9px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: iconBg,
                color: iconFg,
            }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.disabled' }}>
                    {label}
                </Typography>
                {loading ? (
                    <Skeleton variant="text" width={80} height={20} />
                ) : (
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 800, color: 'text.primary' }}>
                        {value}
                    </Typography>
                )}
            </Box>
            {!loading && share && (
                <Typography component="span" sx={{ fontSize: '11px', fontWeight: 700, color: 'text.disabled', whiteSpace: 'nowrap' }}>{share}</Typography>
            )}
        </Box>
    );
}

// `.legend-item` — small dot + label + bold value, used under the vehicle
// type donut. Local copy of SaleDashboard.js's LegendRow.
function LegendRow({ color, label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px', color: 'text.secondary', width: '100%' }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
            <Typography component="span" sx={{ fontSize: '12px', color: 'text.secondary' }}>{label}</Typography>
            <Typography component="span" sx={{ fontSize: '12px', fontWeight: 700, color: 'text.primary', ml: 'auto' }}>{value}</Typography>
        </Box>
    );
}

// DESIGN-SYSTEM.md §5.11 empty state — centered dashed-circle icon + bold
// line + smaller instructional line. Local copy of SaleDashboard.js's.
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
            }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

export default function VehicleDashboard() {
    const isMobileShell = useIsMobileShell();
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [vehicleData, setVehicleData] = useState(EMPTY_VEHICLE_DATA);
    const { hasPermission } = usePermissions();
    // `POST /chart/vehicle` e `/chart/vehicle/values` exigem `report.vehicle`
    // (fv-api/config/router.go), mais restrita que `dashboard.vehicle`. Sem ela
    // nem chamamos (um 403 abriria o modal global): barras mensais, "atendidos" e
    // ticket médio ficam omitidos/"—" e o resto do dashboard segue normal.
    const canReport = hasPermission('report.vehicle');
    const [reportData, setReportData] = useState(null);
    const [vehicleValues, setVehicleValues] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);

    const fetchVehicleStatistics = async () => {
        try {
            const response = await httpClient.get('/vehicles/statistics');
            return response.data || EMPTY_VEHICLE_DATA;
        } catch (error) {
            console.error('Erro ao carregar estatísticas de veículos:', error);
            return EMPTY_VEHICLE_DATA;
        }
    };

    const loadAllData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchVehicleStatistics();
            setVehicleData(data);
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Dados extras da faixa "Indicadores da frota". Cada chamada falha de forma
    // independente (erro => null => card omitido/"—"), sem modal nem quebra.
    const loadReportData = useCallback(async () => {
        if (!canReport || isMobileShell) {
            setReportData(null);
            setVehicleValues(null);
            return;
        }
        setReportLoading(true);
        const [chartRes, valuesRes] = await Promise.allSettled([
            httpClient.post('/chart/vehicle', {}),
            httpClient.post('/chart/vehicle/values'),
        ]);
        setReportData(chartRes.status === 'fulfilled' ? chartRes.value.data || null : null);
        setVehicleValues(valuesRes.status === 'fulfilled' && Array.isArray(valuesRes.value.data) ? valuesRes.value.data : valuesRes.status === 'fulfilled' ? [] : null);
        setReportLoading(false);
    }, [canReport, isMobileShell]);

    const handleRefresh = async () => {
        await Promise.all([loadAllData(), loadReportData()]);
    };

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    useEffect(() => {
        loadReportData();
    }, [loadReportData]);

    // No mobile, o refresh vai para o AppBar (e sai da tela).
    useAppBarAction({
        label: 'Atualizar',
        icon: RefreshCw,
        onClick: handleRefresh,
        disabled: loading,
        spinning: loading,
        enabled: isMobileShell,
    });

    const formatNumber = (num) => {
        if (typeof num === 'number') {
            return num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
        }
        return num || '0';
    };

    // Cores por tipo de veículo — mesma ordem/tons de
    // `prototipo-dashboard-veiculos.html` (brand-1/warning/success/purple),
    // mapeados para os tokens do tema (DESIGN-SYSTEM.md §4/§9): Carros =
    // primary, Motos = warning, Bicicletas = success, Outros = secondary.
    const vehicleTypeData = [
        { id: 0, value: vehicleData.total_cars, label: 'Carros', color: theme.palette.primary.main },
        { id: 1, value: vehicleData.total_motorcycles, label: 'Motos', color: theme.palette.warning.main },
        { id: 2, value: vehicleData.total_bicycles, label: 'Bicicletas', color: theme.palette.success.main },
        { id: 3, value: vehicleData.total_other_vehicles, label: 'Outros', color: theme.palette.secondary.main },
    ].filter((item) => item.value > 0);
    const hasTypeData = vehicleTypeData.length > 0;

    // `VehicleServiceInfo` (fv-api/vehicle/repository.go) não traz um `id` —
    // `DefaultTable` usa `row.id` como key de linha, então preenchemos um
    // índice local só para satisfazer essa exigência de renderização (não
    // altera os dados exibidos nem o payload consumido pela tabela).
    const mostServicesRows = (vehicleData.cars_with_most_services || []).map((row, index) => ({ id: index, ...row }));
    const mostExpensiveRows = (vehicleData.cars_with_most_expensive_services || []).map((row, index) => ({ id: index, ...row }));

    // Cadastros por mês do ano corrente (`monthly_chart` só traz meses com
    // cadastro; completa os 12 com zero).
    const monthlyValues = useMemo(() => {
        if (!reportData) return null;
        const values = new Array(12).fill(0);
        (reportData.monthly_chart || []).forEach((item) => {
            const idx = Number(item.x) - 1;
            if (idx >= 0 && idx < 12) values[idx] = Number(item.y) || 0;
        });
        return values;
    }, [reportData]);
    const monthlyTotal = monthlyValues ? monthlyValues.reduce((a, b) => a + b, 0) : 0;
    const monthlyPeak = monthlyValues && monthlyTotal > 0 ? monthlyValues.indexOf(Math.max(...monthlyValues)) : -1;
    const now = new Date();

    // Veículos atendidos = ativos com ao menos um item de OS (produto/serviço);
    // ticket médio = média de (produtos + serviços) entre eles.
    const attendedCount = vehicleValues ? vehicleValues.length : null;
    const averageTicket = useMemo(() => {
        if (!vehicleValues) return null;
        if (vehicleValues.length === 0) return 0;
        const total = vehicleValues.reduce((sum, v) => sum + parseDecimal(v.total_products_value) + parseDecimal(v.total_services_value), 0);
        return total / vehicleValues.length;
    }, [vehicleValues]);

    // % do fabricante mais popular sobre a frota: `manufacturer_chart` (mesma
    // base — ativos + inativos — de `total_vehicles`). `GET /vehicles` só lista
    // ativos, por isso não serve de denominador.
    const manufacturerPct = useMemo(() => {
        const total = vehicleData.total_vehicles;
        const name = vehicleData.most_popular_manufacturer;
        if (!reportData || !name || !total) return null;
        const match = (reportData.manufacturer_chart || []).find((m) => normalizeText(m.label) === normalizeText(name));
        return match ? Math.round((match.value / total) * 100) : null;
    }, [reportData, vehicleData.total_vehicles, vehicleData.most_popular_manufacturer]);

    const totalV = vehicleData.total_vehicles;
    const activePct = totalV > 0 ? (vehicleData.active_vehicles / totalV) * 100 : 0;
    const inactivePct = totalV > 0 ? (vehicleData.inactive_vehicles / totalV) * 100 : 0;
    const typeShare = (n) => (totalV > 0 ? `${Math.round((n / totalV) * 100)}% da frota` : undefined);
    const cardsLoading = loading;
    const extraLoading = loading || reportLoading;

    useAppBarSubtitle(isMobileShell ? 'Dashboard' : null);

    if (isMobileShell) {
        const CarIcon = protoIcon('car');
        const typeItems = [
            { name: 'Carros', value: vehicleData.total_cars },
            { name: 'Motos', value: vehicleData.total_motorcycles },
            { name: 'Bicicletas', value: vehicleData.total_bicycles },
            { name: 'Outros', value: vehicleData.total_other_vehicles },
        ];
        const renderRows = (rows, sub, end) => (
            <KitList>
                {rows.map((row) => (
                    <KitItem
                        key={row.id}
                        icon={CarIcon}
                        title={`${row.vehicle_name} · ${row.license_plate}`}
                        sub={sub(row)}
                        end={end(row)}
                    />
                ))}
            </KitList>
        );
        return (
            <PageScaffold title="Dashboard de Veículos" titlePageTarget="Veículos" links={[{ title: "Dashboard", link: "" }]}>
                <KpiGrid first>
                    <Kpi label="Veículos" icon="car" value={formatNumber(vehicleData.total_vehicles)} loading={loading} />
                    <Kpi label="Ativos" icon="check" value={formatNumber(vehicleData.active_vehicles)} loading={loading} />
                    <Kpi label="Inativos" icon="x" value={formatNumber(vehicleData.inactive_vehicles)} delta={{ text: '', tone: 'down' }} loading={loading} />
                    <Kpi label="Km médio" icon="trendup" value={`${formatNumber(vehicleData.average_kilometer)} km`} loading={loading} />
                </KpiGrid>
                <SecH title="Tipos de veículos" />
                <KitCard>
                    {hasTypeData ? <HBars items={typeItems} /> : <KitEmptyNote>Cadastre veículos para visualizar a distribuição por tipo</KitEmptyNote>}
                </KitCard>
                <SecH title="Métricas gerais" />
                <KitCard sx={{ py: '7px' }}>
                    <KitKV label="Idade média" value={`${formatNumber(vehicleData.average_age)} anos`} />
                    <KitKV label="Cor mais popular" value={vehicleData.most_popular_color || 'N/A'} />
                    <KitKV label="Fabricante mais popular" value={vehicleData.most_popular_manufacturer || 'N/A'} />
                </KitCard>
                {mostServicesRows.length > 0 && (
                    <>
                        <SecH title="Veículos com mais serviços" />
                        {renderRows(mostServicesRows, (r) => `${r.service_count} serviços`, (r) => <KitPill tone="info">{r.service_count}</KitPill>)}
                    </>
                )}
                {mostExpensiveRows.length > 0 && (
                    <>
                        <SecH title="Serviços mais caros" />
                        {renderRows(mostExpensiveRows, () => 'Valor total', (r) => <KitPill tone="success">R$ {r.total_value}</KitPill>)}
                    </>
                )}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold title="Dashboard de Veículos" titlePageTarget="Veículos" links={[{
            title: "Dashboard",
            link: ""
        }]}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone + título + descrição + atualizar (DESIGN-SYSTEM.md §3.3/§6.1, prototipo-dashboard-veiculos.html). */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <Car size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Dashboard de Veículos
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Análise completa da frota cadastrada.
                            </Typography>
                        </Box>
                    </Box>

                    {!isMobileShell && (
                    <Tooltip title="Atualizar dados">
                        <span>
                            <IconButton
                                onClick={handleRefresh}
                                disabled={loading}
                                sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: '10px',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    color: 'text.secondary',
                                    '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'background.subtle' },
                                }}
                            >
                                <RefreshCw size={16} strokeWidth={2} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    )}
                </Box>

                {/* Indicadores da frota — hero "Veículos cadastrados" + 6 tickers
                    (prototipo-dashboard-veiculos.html). Frota cadastrada hoje, sem
                    filtro de período (a API não filtra). */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: '10px' }}>
                    <Typography component="h3" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>Indicadores da frota</Typography>
                    <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled' }}>Frota cadastrada hoje · sem filtro de período</Typography>
                </Box>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                    gap: '10px',
                    mb: 2,
                    '& > *': { gridColumn: { xs: 'span 12', sm: 'span 6', lg: 'span 3' }, minWidth: 0 },
                    '& > :first-of-type': { gridColumn: { xs: 'span 12', lg: 'span 6' }, gridRow: { lg: 'span 3' } },
                }}>
                    {/* Hero — Veículos cadastrados. */}
                    <Card sx={{ borderLeft: '4px solid', borderLeftColor: 'primary.main', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ p: '14px 18px', '&:last-child': { pb: '14px' }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: '6px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11px', fontWeight: 600, color: 'text.secondary' }}>
                                    <Box sx={{ width: 20, height: 20, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'info.bg', color: 'primary.main' }}>
                                        <Car size={13} strokeWidth={2} />
                                    </Box>
                                    Status dos Veículos · Veículos cadastrados
                                </Box>
                                {!loading && totalV > 0 && (
                                    <Box title="Veículos ativos sobre o total" sx={{ fontSize: '10.5px', fontWeight: 700, px: '8px', py: '1px', borderRadius: '20px', color: 'success.main', bgcolor: 'success.bg', whiteSpace: 'nowrap' }}>
                                        {formatPct(activePct)} ativos
                                    </Box>
                                )}
                            </Box>
                            {loading ? (
                                <Skeleton variant="text" width={120} height={40} />
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontSize: { xs: '26px', sm: '30px' }, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                                        <AnimatedNumber value={totalV} format={String} />
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>Total de veículos</Typography>
                                </Box>
                            )}

                            {extraLoading && canReport ? (
                                <Skeleton variant="rounded" height={96} sx={{ mt: 1.5, mb: 1.5 }} />
                            ) : monthlyValues && (
                                <>
                                    <MonthlyBars values={monthlyValues} currentMonth={now.getMonth()} />
                                    <Box sx={{ fontSize: '10.5px', color: 'text.disabled', mb: 1.5, '& b': { color: 'text.secondary', fontWeight: 600 } }}>
                                        Cadastros por mês em <b>{now.getFullYear()}</b> · {monthlyTotal} no ano{monthlyPeak >= 0 && <> · pico em <b>{MONTH_ABBR_LABELS[monthlyPeak]} ({monthlyValues[monthlyPeak]})</b></>}
                                    </Box>
                                </>
                            )}

                            <Box sx={{ mt: 'auto', pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                                <Box title="Ativos x inativos" sx={{ display: 'flex', height: 8, borderRadius: '6px', overflow: 'hidden', gap: '2px', bgcolor: 'background.subtle', mb: '6px' }}>
                                    <Box sx={{ width: `${activePct}%`, height: '100%', bgcolor: 'success.main' }} />
                                    <Box sx={{ width: `${inactivePct}%`, height: '100%', bgcolor: 'error.main' }} />
                                </Box>
                                <SummaryRow
                                    icon={<CheckCircle2 size={13} strokeWidth={2} />}
                                    iconBg="success.bg" iconFg="success.main"
                                    label="Veículos ativos"
                                    share={totalV > 0 ? `${formatPct(activePct)} da frota` : undefined}
                                    value={vehicleData.active_vehicles}
                                    loading={loading}
                                />
                                <SummaryRow
                                    icon={<XCircle size={13} strokeWidth={2} />}
                                    iconBg="error.bg" iconFg="error.main"
                                    label="Veículos inativos"
                                    share={totalV > 0 ? `${formatPct(inactivePct)} da frota` : undefined}
                                    value={vehicleData.inactive_vehicles}
                                    loading={loading}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    <FleetTicker
                        icon={<Wrench size={11} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" accent="secondary.main"
                        label="Veículos atendidos (com OS)"
                        value={attendedCount == null ? '—' : attendedCount.toLocaleString('pt-BR')}
                        note="Veículos ativos com item de OS lançado"
                        loading={extraLoading && canReport}
                    />
                    <FleetTicker
                        icon={<DollarSign size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="brand2.main" accent="brand2.main"
                        label="Ticket médio de OS por veículo"
                        value={averageTicket == null ? '—' : `R$ ${averageTicket.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        note="Média entre os veículos atendidos"
                        loading={extraLoading && canReport}
                    />
                    <FleetTicker
                        icon={<Gauge size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" accent="primary.main"
                        label="Km médio"
                        value={`${formatNumber(vehicleData.average_kilometer)} km`}
                        note="Média da frota cadastrada"
                        loading={cardsLoading}
                    />
                    <FleetTicker
                        icon={<CalendarDays size={11} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" accent="warning.main"
                        label="Idade média"
                        value={`${formatNumber(vehicleData.average_age)} anos`}
                        note="Média da frota cadastrada"
                        loading={cardsLoading}
                    />
                    <FleetTicker
                        icon={<Palette size={11} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" accent="secondary.main"
                        label="Cor mais popular"
                        value={vehicleData.most_popular_color || 'N/A'}
                        note="Cor predominante da frota"
                        loading={cardsLoading}
                    />
                    <FleetTicker
                        icon={<Factory size={11} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" accent="success.main"
                        label="Fabricante mais popular"
                        value={vehicleData.most_popular_manufacturer || 'N/A'}
                        note={manufacturerPct != null ? <><b>{manufacturerPct}%</b> da frota</> : 'Fabricante com mais veículos'}
                        loading={cardsLoading}
                    />
                </Box>

                {/* dash-grid.even — Distribuição por Tipo (donut) / Tipos de Veículos. */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    gap: 2,
                    mb: 2,
                }}>
                    {/* Distribuição por Tipo — donut + legenda. */}
                    <Card>
                        <CardContent sx={{ p: 2.25 }}>
                            <CardHeadRow
                                icon={<PieChartIcon size={18} strokeWidth={2} />}
                                iconBg="info.bg"
                                iconFg="info.main"
                                title="Distribuição por Tipo"
                                subtitle="Percentual da frota"
                                loading={loading}
                            />

                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                    <Skeleton variant="circular" width={120} height={120} />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                                        {[1, 2, 3, 4].map((item) => (
                                            <Skeleton key={item} variant="text" width="100%" height={16} />
                                        ))}
                                    </Box>
                                </Box>
                            ) : hasTypeData ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                                    <Box sx={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
                                        <PieChart
                                            series={[{
                                                data: vehicleTypeData,
                                                innerRadius: 36,
                                                outerRadius: 60,
                                                paddingAngle: 2,
                                                cornerRadius: 3,
                                            }]}
                                            height={120}
                                            width={120}
                                            slotProps={{ legend: { hidden: true } }}
                                            tooltip={{ trigger: 'item' }}
                                            sx={{ '& .MuiChartsLegend-root': { display: 'none' } }}
                                        />
                                        {/* donut-center overlay — DESIGN-SYSTEM.md §5.9. */}
                                        <Box sx={{
                                            position: 'absolute',
                                            inset: 18,
                                            borderRadius: '50%',
                                            bgcolor: 'background.paper',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            pointerEvents: 'none',
                                        }}>
                                            <Typography sx={{ fontSize: '17px', fontWeight: 800, color: 'text.primary', lineHeight: 1.1 }}>
                                                {vehicleData.total_vehicles}
                                            </Typography>
                                            <Typography sx={{ fontSize: '9.5px', color: 'text.disabled' }}>veículos</Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, flex: 1, minWidth: 160 }}>
                                        {vehicleTypeData.map((item) => (
                                            <LegendRow
                                                key={item.label}
                                                color={item.color}
                                                label={item.label}
                                                value={`${Math.round((item.value / vehicleData.total_vehicles) * 100)}%`}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            ) : (
                                <EmptyMiniState
                                    icon={<PieChartIcon size={24} strokeWidth={2} />}
                                    title="Nenhum dado disponível"
                                    subtitle="Cadastre veículos para visualizar a distribuição por tipo"
                                />
                            )}
                        </CardContent>
                    </Card>

                    {/* Tipos de Veículos — metric-row (contagem por tipo). */}
                    <Card>
                        <CardContent sx={{ p: 2.25 }}>
                            <CardHeadRow
                                icon={<Layers size={18} strokeWidth={2} />}
                                iconBg="warning.bg"
                                iconFg="warning.main"
                                title="Tipos de Veículos"
                                subtitle="Contagem por tipo"
                                loading={loading}
                            />
                            <MetricRow
                                icon={<Car size={15} strokeWidth={2} />}
                                iconBg="info.bg"
                                iconFg="info.main"
                                label="Carros"
                                value={vehicleData.total_cars}
                                share={typeShare(vehicleData.total_cars)}
                                loading={loading}
                            />
                            <MetricRow
                                icon={<Motorbike size={15} strokeWidth={2} />}
                                iconBg="warning.bg"
                                iconFg="warning.main"
                                label="Motos"
                                value={vehicleData.total_motorcycles}
                                share={typeShare(vehicleData.total_motorcycles)}
                                loading={loading}
                            />
                            <MetricRow
                                icon={<Bike size={15} strokeWidth={2} />}
                                iconBg="success.bg"
                                iconFg="success.main"
                                label="Bicicletas"
                                value={vehicleData.total_bicycles}
                                share={typeShare(vehicleData.total_bicycles)}
                                loading={loading}
                            />
                            <MetricRow
                                icon={<CircleDot size={15} strokeWidth={2} />}
                                iconBg="secondary.bg"
                                iconFg="secondary.main"
                                label="Outros"
                                value={vehicleData.total_other_vehicles}
                                share={typeShare(vehicleData.total_other_vehicles)}
                                loading={loading}
                            />
                        </CardContent>
                    </Card>
                </Box>

                {/* dash-grid.even — tabelas "Mais Serviços" / "Serviços Mais Caros". */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    gap: 2,
                    mb: 2,
                }}>
                    <Card>
                        <CardContent sx={{ p: 2.25 }}>
                            <CardHeadRow
                                icon={<TrendingUp size={18} strokeWidth={2} />}
                                iconBg="info.bg"
                                iconFg="info.main"
                                title="Veículos com Mais Serviços"
                                subtitle="Top 5 · só considera carros"
                                loading={loading}
                            />
                            <DefaultTable
                                colunms={[
                                    { path: 'vehicle_name', name: 'Veículo' },
                                    { path: 'license_plate', name: 'Placa' },
                                    {
                                        path: 'service_count',
                                        name: 'Serviços',
                                        render: (row) => (
                                            <Chip
                                                label={row.service_count}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'info.bg',
                                                    color: 'info.main',
                                                    fontWeight: 800,
                                                    fontSize: '12px',
                                                    minWidth: 32,
                                                }}
                                            />
                                        ),
                                    },
                                ]}
                                data={mostServicesRows}
                                loading={loading}
                                rowPerPage={5}
                                embedded
                            />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ p: 2.25 }}>
                            <CardHeadRow
                                icon={<Wallet size={18} strokeWidth={2} />}
                                iconBg="success.bg"
                                iconFg="success.main"
                                title="Veículos com Serviços Mais Caros"
                                subtitle="Top 5 por valor total · só considera carros"
                                loading={loading}
                            />
                            <DefaultTable
                                colunms={[
                                    { path: 'vehicle_name', name: 'Veículo' },
                                    { path: 'license_plate', name: 'Placa' },
                                    {
                                        path: 'total_value',
                                        name: 'Valor total',
                                        render: (row) => (
                                            <Typography sx={{ fontSize: '13px', fontWeight: 800, color: 'success.main' }}>
                                                R$ {row.total_value}
                                            </Typography>
                                        ),
                                    },
                                ]}
                                data={mostExpensiveRows}
                                loading={loading}
                                rowPerPage={5}
                                embedded
                            />
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </PageScaffold>
    )
}
